import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import SearchableView from "./SearchableView";
import AddOrEditUserAdminView from "./AddOrEditUserAdminView";
import Popup from "./Popup";
import AdminListsRequest from "../models/AdminListsRequest";
import UserType from "../classes/UserType";
import PaginationView from "./PaginationView";
import Query from "../classes/Query";
import NOKUser from "../models/NOKUser";
import MessageDispatch from "../rocket/MessageDispatch";
import Button from "./Button";
import ListWrapper from "../utils/ListWrapper";
import UsersList from "../classes/UsersList";
import UsersListRequest from "../models/UsersListRequest";
import TextView from "./TextView";
import Hospital from "../models/Hospital";
import LabelDropdown from "./LabelDropdown";
import CollectionUtils from "../utils/CollectionUtils";
import { UsageConstants } from "../rocket/D3ETemplate";
import { StyleThemeData } from "./ThemeWrapper";
import { BuildContext } from "../classes/BuildContext";

type _AddButtonOnPressed = (d3eState: UserAdminListRefs) => void;

type _LabelDropdown2OnChanged = (
  text: string,
  d3eState: UserAdminListRefs
) => void;

export interface UserAdminListProps extends BaseUIProps {
  key?: string;
}
/// To store state data for UserAdminList
class UserAdminListRefs {
  public addButton: AddButtonState = new AddButtonState();
}

interface AddButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: UserAdminListRefs;
  _onAddButtonClicked?: _AddButtonOnPressed;
}

class AddButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _AddButtonWithState extends ObservableComponent<AddButtonWithStateProps> {
  addButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AddButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get addButton(): AddButtonState {
    return this.props.d3eState.addButton;
  }
  public get d3eState(): UserAdminListRefs {
    return this.props.d3eState;
  }
  public get _onAddButtonClicked(): _AddButtonOnPressed {
    return this.props._onAddButtonClicked;
  }
  public initState() {
    super.initState();

    this.updateObservable("addButton", null, this.addButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["addButton", "addButton.", "addButton.hover"], this.rebuild);
  }
  public addButtonOnEnter(event): void {
    return this.addButton.setHover(true);
  }
  public addButtonOnExit(event): void {
    return this.addButton.setHover(false);
  }
  public dispose(): void {
    this.addButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.fromLTRB(0.0, 19.0, 0.0, 0.0, new Map()),
      child: Button({
        padding: this.addButton.hover
          ? cStyle.tButtonPrimaryPaddingOnHover
          : cStyle.tButtonPrimaryPaddingOn,
        decoration: this.addButton.hover
          ? cStyle.tButtonPrimaryDecorationOnHover
          : cStyle.tButtonPrimaryDecorationOn,
        disable: this.addButton.disable,
        onPressed: () => {
          this._onAddButtonClicked(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({ data: "+ Add Admin" }),
        onEnter: (event) => {
          this.addButtonOnEnter(event);
        },
        onExit: (event) => {
          this.addButtonOnExit(event);
        },
      }),
      className: "x17e",
    });
  }
}
function AddButtonWithState(props: AddButtonWithStateProps) {
  return React.createElement(_AddButtonWithState, props);
}

class _UserAdminListState extends ObservableComponent<UserAdminListProps> {
  d3eState: UserAdminListRefs = new UserAdminListRefs();
  obj: NOKUser = null;
  query: UsersList = null;
  request: UsersListRequest = null;
  adminsList: Array<NOKUser> = ListWrapper.widget(this, "adminsList");
  status: string = "";
  admins: Array<NOKUser> = ListWrapper.widget(this, "admins");
  hospitals: Array<Hospital> = ListWrapper.widget(this, "hospitals");
  hospital: Hospital = null;
  userValue: NOKUser = null;
  addUserAdminPopup: Popup;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: UserAdminListProps) {
    super(props);

    this.initState();
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.on(
      [
        "request",
        "request.applyStatus",
        "request.ascending",
        "request.hospital",
        "request.name",
        "request.offset",
        "request.orderBy",
        "request.pageSize",
        "request.status",
        "request.user",
        "request.userType",
      ],
      this.computeQuery
    );

    this.computeQuery();

    this.on(["query", "query.items"], this.computeAdminsList);

    this.computeAdminsList();

    this.computeAdmins();

    this.computeHospitals();

    this.on(
      [
        "adminsList",
        "adminsList.email",
        "adminsList.hospital",
        "adminsList.hospital.name",
        "adminsList.mobile",
        "adminsList.name",
        "adminsList.status",
        "hospital",
        "hospitals",
        "query",
        "query.count",
        "query.items",
        "request",
        "request.offset",
        "request.pageSize",
        "status",
      ],
      this.rebuild
    );
  }
  public setObj(val: NOKUser): void {
    let isValChanged: boolean = this.obj !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("obj", this.obj, val);

    this.obj = val;

    this.fire("obj", this);
  }
  public setQuery(val: UsersList): void {
    let isValChanged: boolean = this.query !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("query", this.query, val);

    MessageDispatch.get().dispose(this.query);

    this.query = val;

    this.fire("query", this);
  }
  public computeQuery = async (): Promise<void> => {
    try {
      this.setQuery(
        await Query.get().getUsersList(
          UsageConstants.QUERY_GETUSERSLIST_USERADMINLIST_PROPERTIES_QUERY_COMPUTATION,
          this.request,
          { "synchronize": true }
        )
      );
    } catch (exception) {
      console.log(" exception in computeQuery : " + exception.toString());

      this.setQuery(null);
    }
  };
  public setRequest(val: UsersListRequest): void {
    let isValChanged: boolean = this.request !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("request", this.request, val);

    this.request = val;

    this.fire("request", this);
  }
  public setAdminsList(val: Array<NOKUser>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.adminsList,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("adminsList", this.adminsList, val);

    this.adminsList.clear();

    this.adminsList.addAll(val);

    this.fire("adminsList", this);
  }
  public addToAdminsList(val: NOKUser, index: number = -1): void {
    if (index === -1) {
      if (!this.adminsList.contains(val)) this.adminsList.add(val);
    } else {
      this.adminsList.remove(this.adminsList.elementAt(index));

      this.adminsList.add(val);
    }

    this.fire("adminsList", this, val, true);

    this.updateObservable("adminsList", null, val);
  }
  public removeFromAdminsList(val: NOKUser): void {
    this.adminsList.remove(val);

    this.fire("adminsList", this, val, false);

    this.removeObservable("adminsList", val);
  }
  public computeAdminsList = (): void => {
    try {
      this.setAdminsList(Array.from(this.query?.items ?? []));
    } catch (exception) {
      console.log(" exception in computeAdminsList : " + exception.toString());

      this.setAdminsList([]);
    }
  };
  public setStatus(val: string): void {
    let isValChanged: boolean = this.status !== val;

    if (!isValChanged) {
      return;
    }

    this.status = val;

    this.fire("status", this);
  }
  public setAdmins(val: Array<NOKUser>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(this.admins, val);

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("admins", this.admins, val);

    this.admins.clear();

    this.admins.addAll(val);

    this.fire("admins", this);
  }
  public addToAdmins(val: NOKUser, index: number = -1): void {
    if (index === -1) {
      if (!this.admins.contains(val)) this.admins.add(val);
    } else {
      this.admins.remove(this.admins.elementAt(index));

      this.admins.add(val);
    }

    this.fire("admins", this, val, true);

    this.updateObservable("admins", null, val);
  }
  public removeFromAdmins(val: NOKUser): void {
    this.admins.remove(val);

    this.fire("admins", this, val, false);

    this.removeObservable("admins", val);
  }
  public computeAdmins = async (): Promise<void> => {
    try {
      this.setAdmins(
        Array.from(
          (
            await Query.get().getAdminLists(
              UsageConstants.QUERY_GETADMINLISTS_USERADMINLIST_PROPERTIES_ADMINS_COMPUTATION,
              new AdminListsRequest({ userType: UserType.Admin })
            )
          )?.items ?? []
        )
      );
    } catch (exception) {
      console.log(" exception in computeAdmins : " + exception.toString());

      this.setAdmins([]);
    }
  };
  public setHospitals(val: Array<Hospital>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.hospitals,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("hospitals", this.hospitals, val);

    this.hospitals.clear();

    this.hospitals.addAll(val);

    this.fire("hospitals", this);
  }
  public addToHospitals(val: Hospital, index: number = -1): void {
    if (index === -1) {
      if (!this.hospitals.contains(val)) this.hospitals.add(val);
    } else {
      this.hospitals.remove(this.hospitals.elementAt(index));

      this.hospitals.add(val);
    }

    this.fire("hospitals", this, val, true);

    this.updateObservable("hospitals", null, val);
  }
  public removeFromHospitals(val: Hospital): void {
    this.hospitals.remove(val);

    this.fire("hospitals", this, val, false);

    this.removeObservable("hospitals", val);
  }
  public computeHospitals = async (): Promise<void> => {
    try {
      this.setHospitals(
        Array.from(
          (
            await Query.get().getHosiptalList(
              UsageConstants.QUERY_GETHOSIPTALLIST_USERADMINLIST_PROPERTIES_HOSPITALS_COMPUTATION
            )
          )?.items ?? []
        )
      );
    } catch (exception) {
      console.log(" exception in computeHospitals : " + exception.toString());

      this.setHospitals([]);
    }
  };
  public setHospital(val: Hospital): void {
    let isValChanged: boolean = this.hospital !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("hospital", this.hospital, val);

    this.hospital = val;

    this.fire("hospital", this);
  }
  public setUserValue(val: NOKUser): void {
    let isValChanged: boolean = this.userValue !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("userValue", this.userValue, val);

    this.userValue = val;

    this.fire("userValue", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      mainAxisAlignment: ui.MainAxisAlignment.start,
      crossAxisAlignment: ui.CrossAxisAlignment.start,
      children: [
        ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
          children: [
            ui.Row({
              children: [
                ui.Container({
                  margin: ui.EdgeInsets.fromLTRB(
                    0.0,
                    0.0,
                    10.0,
                    9.0,
                    new Map()
                  ),
                  width: 250,
                  child: SearchableView<Hospital>({
                    placeHolder: "Search",
                    value: this.hospital,
                    items: this.hospitals,
                    name: "Hospital",
                    onChanged: (item) => {
                      this.onChangedLabelDropdown3Handler(item, this.d3eState);
                    },
                  }),
                  key: "0",
                  className: "x4d5 hc",
                }),
                ui.Container({
                  margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 0.0, 5.0, new Map()),
                  child: LabelDropdown<string>({
                    placeHolder: "---Select---",
                    name: "Status",
                    value: this.status,
                    items: ["Active", "Inactive"],
                    width: 250,
                    onChanged: (text) => {
                      this.labelDropdown2onChanged(text, this.d3eState);

                      this.onChangedLabelDropdown1Handler2(text, this.d3eState);
                    },
                    builder: (context, item) => {
                      return TextView({ data: item === null ? "All" : item });
                    },
                  }),
                  key: "1",
                  className: "xd20",
                }),
              ],
              key: "0",
            }),
            ui.Row({
              children: [
                AddButtonWithState({
                  d3eState: this.d3eState,
                  _onAddButtonClicked: this.onAddButtonClicked,
                  key: "0",
                }),
              ],
              key: "1",
            }),
          ],
          className: "x318 hc h",
          key: "0",
        }),
        ui.Column({
          children: [
            ui.Column({
              children: [
                ui.Table({
                  defaultColumnWidth: new ui.FlexColumnWidth(1),
                  columnWidths: {
                    0: new ui.FlexColumnWidth(1),
                    1: new ui.FlexColumnWidth(1),
                    2: new ui.FlexColumnWidth(1),
                    3: new ui.FlexColumnWidth(1),
                    4: new ui.FixedColumnWidth(100),
                  },
                  children: [
                    ui.TableRow({
                      children: [
                        ui.TableCell({
                          verticalAlignment:
                            ui.TableCellVerticalAlignment.middle,
                          child: TextView({
                            data: "Admin",
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "0",
                        }),
                        ui.TableCell({
                          verticalAlignment:
                            ui.TableCellVerticalAlignment.middle,
                          child: TextView({
                            data: "Hospital",
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "1",
                        }),
                        ui.TableCell({
                          verticalAlignment:
                            ui.TableCellVerticalAlignment.middle,
                          child: TextView({
                            data: "Email ID",
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "2",
                        }),
                        ui.TableCell({
                          verticalAlignment:
                            ui.TableCellVerticalAlignment.middle,
                          child: TextView({
                            data: "Contact Number",
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "3",
                        }),
                        ui.TableCell({
                          verticalAlignment:
                            ui.TableCellVerticalAlignment.middle,
                          child: TextView({
                            data: "Status",
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "4",
                        }),
                      ],
                      className: "xcf6 hc",
                      key: "0",
                    }),
                    this.query !== null && this.query.items.isNotEmpty
                      ? this.adminsList.expand((forItem) => [
                          ui.TableRow({
                            children: [
                              ui.TableCell({
                                verticalAlignment:
                                  ui.TableCellVerticalAlignment.middle,
                                child: TextView({
                                  data: forItem.name,
                                  softWrap:
                                    cStyle.tTextViewTableCellTextSoftWrapOn,
                                  style: new ui.TextStyle({
                                    fontFamily:
                                      cStyle.tTextViewTableCellTextFontFamilyOn,
                                    color: cStyle.tTextViewTableCellTextColorOn,
                                    fontSize:
                                      cStyle.tTextViewTableCellTextFontSizeOn,
                                  }),
                                  className: "hc",
                                }),
                                className: "hc",
                                key: "0",
                              }),
                              ui.TableCell({
                                verticalAlignment:
                                  ui.TableCellVerticalAlignment.middle,
                                child: TextView({
                                  data: forItem.hospital.name,
                                  softWrap:
                                    cStyle.tTextViewTableCellTextSoftWrapOn,
                                  style: new ui.TextStyle({
                                    fontFamily:
                                      cStyle.tTextViewTableCellTextFontFamilyOn,
                                    color: cStyle.tTextViewTableCellTextColorOn,
                                    fontSize:
                                      cStyle.tTextViewTableCellTextFontSizeOn,
                                  }),
                                  className: "hc",
                                }),
                                className: "hc",
                                key: "1",
                              }),
                              ui.TableCell({
                                verticalAlignment:
                                  ui.TableCellVerticalAlignment.middle,
                                child: TextView({
                                  data: forItem.email,
                                  softWrap:
                                    cStyle.tTextViewTableCellTextSoftWrapOn,
                                  style: new ui.TextStyle({
                                    fontFamily:
                                      cStyle.tTextViewTableCellTextFontFamilyOn,
                                    color: cStyle.tTextViewTableCellTextColorOn,
                                    fontSize:
                                      cStyle.tTextViewTableCellTextFontSizeOn,
                                  }),
                                  className: "hc",
                                }),
                                className: "hc",
                                key: "2",
                              }),
                              ui.TableCell({
                                verticalAlignment:
                                  ui.TableCellVerticalAlignment.middle,
                                child: TextView({
                                  data: forItem.mobile,
                                  softWrap:
                                    cStyle.tTextViewTableCellTextSoftWrapOn,
                                  style: new ui.TextStyle({
                                    fontFamily:
                                      cStyle.tTextViewTableCellTextFontFamilyOn,
                                    color: cStyle.tTextViewTableCellTextColorOn,
                                    fontSize:
                                      cStyle.tTextViewTableCellTextFontSizeOn,
                                  }),
                                  className: "hc",
                                }),
                                className: "hc",
                                key: "3",
                              }),
                              ui.TableCell({
                                verticalAlignment:
                                  ui.TableCellVerticalAlignment.middle,
                                child: TextView({
                                  data: forItem.status ? "Active" : "Inactive",
                                  softWrap:
                                    cStyle.tTextViewTableCellTextSoftWrapOn,
                                  states: ui.joinStates(
                                    { "data-c0": forItem.status === false },
                                    {}
                                  ),
                                  style: new ui.TextStyle({
                                    color:
                                      forItem.status === false
                                        ? cStyle.c25
                                        : cStyle.c24,
                                    fontFamily:
                                      cStyle.tTextViewTableCellTextFontFamilyOn,
                                    fontSize:
                                      cStyle.tTextViewTableCellTextFontSizeOn,
                                  }),
                                  className: "xa33 hc",
                                }),
                                className: "hc",
                                key: "4",
                              }),
                            ],
                            onTap: (e) => {
                              e.stopPropagation();

                              this.onEditHandler(forItem, this.d3eState);
                            },
                            className: "xaec4 hc",
                            key: forItem?.ident,
                          }),
                        ])
                      : [],
                  ],
                  className: "xa5d hc h",
                  key: "0",
                }),
                this.query !== null && this.query.items.isEmpty
                  ? ui.Column({
                      mainAxisAlignment: ui.MainAxisAlignment.center,
                      children: [
                        ui.AssetImage({
                          path: "images/emptyList.png",
                          width: 300,
                          height: 300,
                          className: "x8d7 hc vc",
                          key: "0",
                        }),
                        TextView({
                          data: "No Records",
                          style: new ui.TextStyle({
                            fontWeight: ui.FontWeight.bold,
                            fontSize: 16,
                          }),
                          className: "x6e3 hc",
                          key: "1",
                        }),
                      ],
                      className: "x7534 hc",
                    })
                  : [],
                ui.Row({
                  mainAxisAlignment: ui.MainAxisAlignment.end,
                  children: [
                    PaginationView({
                      pageSize: this.request.pageSize,
                      total: this.query !== null ? this.query.count : 0,
                      offset: this.request.offset,
                      onChange: (pageSize, offset) => {
                        this.onPaginationViewChanged(
                          pageSize,
                          offset,
                          this.d3eState
                        );
                      },
                      states: ui.joinStates(
                        {
                          "data-visibility":
                            this.query !== null && this.query.items.isNotEmpty,
                        },
                        {}
                      ),
                      key: "0",
                    }),
                  ],
                  className: "x21b hc h",
                  key: "2",
                }),
              ],
              className: "x148 hc h",
              key: "0",
            }),
          ],
          className: "x8fc hc h",
          key: "1",
        }),
      ],
      className: ui.join(this.props.className, "UserAdminList x086 hc vc h v"),
      ...copyBaseUIProps(this.props),
    });
  }
  public onInit = (): void => {
    this.setUserValue(null);

    this.setHospital(null);

    this.setStatus("Active");

    this.setRequest(
      new UsersListRequest({
        pageSize: 50,
        offset: 0,
        orderBy: "sortOrder",
        ascending: true,
        userType: UserType.Admin,
        applyStatus: true,
        user: this.userValue,
        status: this.status === "Active" ? true : false,
      })
    );
  };
  public onPaginationViewChanged = (
    pageSize: number,
    offset: number,
    d3eState: UserAdminListRefs
  ): void => {
    this.request.setPageSize(pageSize);

    this.request.setOffset(offset);

    this.setRequest(this.request.deepClone());
  };
  public onEditHandler = (
    forItem: NOKUser,
    d3eState: UserAdminListRefs
  ): void => {
    this.setObj(forItem);

    this.showAddUserAdmin();
  };
  public onAddButtonClicked = (d3eState: UserAdminListRefs): void => {
    this.setObj(new NOKUser({ userType: UserType.Admin }));

    this.showAddUserAdmin();
  };
  public onChangedLabelDropdown3Handler = (
    item: Hospital,
    d3eState: UserAdminListRefs
  ): void => {
    this.setHospital(item);

    this.setRequest(
      new UsersListRequest({
        pageSize: 50,
        offset: 0,
        orderBy: "sortOrder",
        ascending: true,
        userType: UserType.Admin,
        user: this.obj,
        applyStatus: true,
        hospital: item,
        status: this.status === "Active" ? true : false,
      })
    );

    this.setRequest(this.request.deepClone());
  };
  public onChangedLabelDropdown1Handler2 = (
    value: string,
    d3eState: UserAdminListRefs
  ): void => {
    this.setStatus(value);

    let val: boolean;

    if (value === "Active") {
      val = true;
    } else {
      val = false;
    }

    this.setRequest(
      new UsersListRequest({
        pageSize: 50,
        offset: 0,
        orderBy: "sortOrder",
        ascending: true,
        userType: UserType.Admin,
        user: this.obj,
        applyStatus: true,
        status: val,
      })
    );

    this.setRequest(this.request.deepClone());
  };
  public onAddUserAdminClosed = (): void => {
    this.setRequest(this.request.deepClone());
  };
  public dispose(): void {
    MessageDispatch.get().dispose(this.query);

    this.addUserAdminPopup?.dispose();

    super.dispose();
  }
  public showAddUserAdmin(
    d3eParams?: Partial<{
      autoClose: boolean;
      model: boolean;
      float: boolean;
      takeFocus: boolean;
    }>
  ): void {
    let autoClose = d3eParams?.autoClose;

    let model = d3eParams?.model;

    let float = d3eParams?.float;

    let takeFocus = d3eParams?.takeFocus;

    this.addUserAdminPopup?.dispose();

    /*
TODO ThemeWrapper.of(this.context);
*/

    let cStyle = StyleThemeData.current;

    this.addUserAdminPopup = new Popup({
      autoClose: autoClose,
      model: model,
      float: float,
      takeFocus: takeFocus,
      position: ui.PopUpPosition.Center,
      child: ui.Container({
        width: 900,
        child: AddOrEditUserAdminView({
          obj: this.obj,
          hospitals: this.hospitals,
          onReload: () => {
            this.onAddUserAdminClosed();
          },
        }),
        className: "xf5a hc vc",
      }),
    });

    this.addUserAdminPopup.showPopup(this.context);
  }
  public hideAddUserAdmin(): void {
    this.addUserAdminPopup?.dispose();
  }
  public labelDropdown2onChanged = (
    val: string,
    d3eState: UserAdminListRefs
  ): void => {
    this.setStatus(val);
  };
  public get addButton() {
    return this.d3eState.addButton;
  }
}
export default function UserAdminList(props: UserAdminListProps) {
  return React.createElement(_UserAdminListState, props);
}
